import React, { useMemo } from 'react';
import {
  GroupingState,
  IntegratedGrouping,
  Appointments as AppointmentsBase,
  AppointmentTooltip as AppointmentTooltipBase,
  TodayButton,
  ViewState,
} from '@devexpress/dx-react-scheduler';
import {
  Scheduler,
  Appointments,
  MonthView,
  WeekView,
  Toolbar,
  ViewSwitcher,
  DayView,
  DateNavigator,
  AppointmentTooltip,
  AllDayPanel,
  GroupingPanel,
  Resources,
} from '@devexpress/dx-react-scheduler-material-ui';
import { makeStyles } from 'tss-react/mui';
import { useCallback, useContext, useState } from 'react';

import {
  SchedulerContext,
  SchedulerContextProvider,
} from '.././SchedulerContext';
import { TSelectedDetail } from '../../../../../../../Types/MasterDetailTypes';
import { useDeepCompareMemo } from 'use-deep-compare';
import { IResourceInfo } from '../../Utils/KalenderTypes';
import { MeQuery } from '../../Queries/MeQuery/_QueryTypes/MeQuery';
import {
  AppointmentComponent,
  IBookingType,
} from './Components/AppointmentComponent';
import { AppointmentContainer } from './Components/AppointmentContainer';
import { CustomAppointments } from './Components/CustomAppointments';
import { CustomMonthAppointmentLayerComponent } from './Components/CustomMonthAppointmentLayerComponent';
import { CustomMonthLayout } from './Components/CustomMonthLayout';
import { CustomTooltipComponent } from './Components/CustomTooltipComponent';
import { DayViewLayoutComponent } from './Components/DayViewLayoutComponent';
import { DayViewTimeScaleLayoutCustom } from './Components/DayViewTimeScaleLayoutCustom';
import { DayViewTimeTableCellComponent } from './Components/DayViewTimeTableCellComponent';
import { DayViewTimeTableLayout } from './Components/DayViewTimeTableLayout';
import { MonthDayScaleLayoutCustom } from './Components/MonthDayScaleLayoutCustom';
import { MonthViewTimeTableCellComponent } from './Components/MonthViewTimeTableCellComponent';
import { MonthViewTimeTableLayoutProps } from './Components/MonthViewTimeTableLayoutProps';
import { TooltipLayoutComponent } from './Components/TooltipLayoutComponent';
import { WeekViewDayScaleLayoutCustom } from './Components/WeekViewDayScaleLayoutCustom';
import { WeekViewTimeScaleLayoutCustom } from './Components/WeekViewTimeScaleLayoutCustom';
import { WeekViewTimeTableCellComponent } from './Components/WeekViewTimeTableCellComponent';
import { WeekViewTimeTableLayoutCustom } from './Components/WeekViewTimeTableLayoutCustom';
import { DateNavigatorRootComponent } from './Components/DateNavigatorRootComponent';
import { AllDayTitleCellComponent } from './Components/AllDayTitleCellComponent';
import { AllDayCellComponent } from './Components/AllDayCellComponent';
import { SwitcherComponent } from './Components/SwitcherComponent';
import { ToolbarRootComponent } from './Components/ToolbarRootComponent';
import { GroupingPanelCellComponent } from './Components/GroupingPanelCellComponent';
import { ToolbarFlexibleSpaceComponent } from './Components/ToolbarFlexibleSpaceComponent';
import { DayWeekViewDayScaleCellCustom } from './Components/DayWeekViewDayScaleCellCustom';
import { DayScaleRowComponentCustom } from './Components/DayScaleRowComponentCustom';
import { INyAktivitetInitialValues } from './Utils/NyAktivitetInitialValues';
import { DateNavigationOpenButtonComponent } from './Components/DateNavigationOpenButtonComponent';
import { DateNavigationButtonComponent } from './Components/DateNavigationButtonComponent';
import { Swipe } from 'react-swipe-component';
import { ViewDbSettingsContext } from './ViewDbSettingsContext';
import {
  useIsExtraSmallScreen,
  useIsSmallScreen,
} from '../../../../../../../Hooks/useFullscreenView';
import { DateNavigationOverlayComponent } from './Components/DateNavigatorOverlayComponent';
import { weekNo } from '../../../../../../../Utils/dateUtils';
import { TodayButtonComponent } from './Components/TodayButton';
import { tryStrToDateTime } from '../../../../../../../Utils/stringUtils';
// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles<
  void,
  'unsetOverflowY' | 'timeScaleContainerCustom'
>()((theme, _params, classes) => ({
  schedulerRootComponent: {
    height: '100%',
    boxShadow:
      '0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)',
    [`&.${classes.unsetOverflowY}`]: {},
    [`&.${classes.timeScaleContainerCustom}`]: {
      '& > :nth-of-type(2)': {
        height: '100%',
        '& > :first-of-type': {
          display: 'flex',
          flexDirection: 'column',
          '& > :nth-of-type(2)': {
            display: 'flex',
            flexBasis: '100%',
          },
        },
      },
    },
  },
  unsetOverflowY: {},
  timeScaleContainerCustom: {},
  groupingPanelCellComponent: {
    [theme.breakpoints.down('md')]: {
      minWidth: 'unset !important',
      maxWidth: '100px',
    },
    '& > :first-of.type': {
      width: 'fit-content',
      '& > :first-of-type': {
        maxWidth: '160px',
        textAlign: 'center',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        [theme.breakpoints.down('md')]: {
          fontSize: '7px',
          maxWidth: 70,
        },
      },
    },
  },

  schedulerMonthViewCustom: {
    '& > :nth-of-type(2)': {
      height: '100%',
      '& >:first-of-type': {
        height: '100%',
        '& > :nth-of-type(2)': {
          height: 'calc(100% - 29px)',
        },
      },
    },
  },
}));

export interface IXBridgeBookingTyp {
  startDate: string;
  endDate: string;
  title?: string | null;
  epost: string[];
  color: boolean;
}

export type XBridgeBookingsObject = { [id: string]: IXBridgeBookingTyp };

export type BookingsObject = { [id: string]: IBookingType };

export type KalenderTooltipHandler = (
  appointmentData?: IBookingType
) => React.ReactElement<any>;

interface IScheduler {
  messages?: any;
  firstDayOfWeek?: number | undefined;
  dayViewDisplayName?: string | undefined;
  weekViewDisplayName?: string | undefined;
  monthViewDisplayName?: string | undefined;
  currentDate: Date;
  onCurrentDateChange?: (currentDate: Date) => void;
  currentView: string;
  onCurrentViewChange?: (currentView: string) => void;
  bookingsObject: BookingsObject;
  resources?: IResourceInfo[];
  redDays?: Date[];
  createNewBookingDialog?: boolean;
  weekViewCellDuration?: number | undefined;
  dayViewCellDuration?: number | undefined;
  overView?: boolean;
  onOverViewChange?: (overView: boolean) => void;
  selectDetail?: (id: TSelectedDetail, force?: boolean) => void;
  bookingGroups: MeQuery | undefined;
  showOutlookBookings?: boolean;
  setShowOutlookBookings?: (showOutlookBookings: boolean) => void;
  showHeader?: boolean;
  oldView?: boolean;
  showViewSwitcher?: boolean;
}

const SchedulerRootComponent = (props: Scheduler.RootProps) => {
  const { classes, cx } = useStyles();
  const schedulerContext = useContext(SchedulerContext);

  return (
    <Scheduler.Root
      className={cx(
        classes.schedulerRootComponent,
        classes.timeScaleContainerCustom,
        {
          [classes.schedulerMonthViewCustom]:
            schedulerContext.currentViewName === 'Month',
        }
      )}
      {...props}
    ></Scheduler.Root>
  );
};

const removeDuplicates = (arr: any[]) => {
  return arr.filter((item, index) => arr.indexOf(item) === index);
};

function checkBookings(
  currentView: string,
  bookings: BookingsObject,
  overView: boolean | undefined,
  appointmentLimitMonthView: { [id: string]: number }
) {
  const IsInAppointmentLimit = (startDate: Date, endDate: Date): number => {
    startDate.setHours(0, 0, 0);
    endDate.setHours(0, 0, 0);
    let fitsInDateCell = 1;
    Object.keys(appointmentLimitMonthView).forEach(element => {
      let limitedDate = new Date(element);
      if (limitedDate >= startDate && limitedDate <= endDate) {
        fitsInDateCell = appointmentLimitMonthView[element];
        appointmentLimitMonthView[element] -= 1;
      }
    });
    return fitsInDateCell;
  };
  if (currentView === 'Month') {
    let limitedBookingObject: { [id: string]: IBookingType } = {};
    Object.keys(bookings).forEach(key => {
      let currentBooking = bookings[key];

      if (overView && currentBooking.allDay) {
        currentBooking = {
          ...currentBooking,
          resourceNameList: removeDuplicates(currentBooking.resourceNameList),
        };
      }

      const limit = IsInAppointmentLimit(
        new Date(currentBooking.startDate),
        new Date(currentBooking.endDate)
      );
      if (limit > 0) {
        limitedBookingObject[key] = currentBooking;
      } else if (limit === 0) {
        limitedBookingObject[key] = {
          ...currentBooking,
          startDate: currentBooking.startDate.split(' ')[0] + ' 23:58',
          endDate: currentBooking.endDate.split(' ')[0] + ' 23:59',
          title: '...',
          mooreBookings: true,
        };
      }
    });
    return Object.keys(limitedBookingObject).map(
      key => limitedBookingObject[key]
    );
  } else {
    Object.keys(bookings).forEach(key => {
      const currentBooking = bookings[key];

      if (overView && currentBooking.allDay) {
        currentBooking.resourceNameList = removeDuplicates(
          currentBooking.resourceNameList
        );
      }
    });
    return Object.keys(bookings).map(key => bookings[key]);
  }
}

// ******** Scheduler Component ********** //

const SchedulerComponent = (props: IScheduler) => {
  const { selectDetail } = props;
  const currentView = props.currentView;
  const overView = props.resources ? props.overView : false;
  const onCurrentDateChange = props.onCurrentDateChange;
  const onCurrentViewChange = props.onCurrentViewChange;
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const viewDbSettingsContext = useContext(ViewDbSettingsContext);
  const viewDbSettingsSetNyAktivitetInitialValues =
    viewDbSettingsContext.setNyAktivitetInitialValues;
  const setNyAktivitetInitialValues = useCallback(
    (value: INyAktivitetInitialValues | undefined) => {
      viewDbSettingsSetNyAktivitetInitialValues(value);
    },
    [viewDbSettingsSetNyAktivitetInitialValues]
  );
  const [onNavigate, setOnNavigate] = useState<
    ((direction: 'forward' | 'back' | undefined) => any) | null
  >(null);

  const showOutlookBookings = props.showOutlookBookings;

  const weekno =
    viewDbSettingsContext.currentDate !== undefined
      ? weekNo(viewDbSettingsContext.currentDate)
      : weekNo(new Date());

  const isSmallScreen = useIsSmallScreen();
  const isExtraSmallScreen = useIsExtraSmallScreen();

  const [appointmentLimitMonthView, dayScaleRecordMonthView, bookings] =
    useMemo(() => {
      const tempAppointmentLimitMonthView: Record<string, number> = {},
        tempDayScaleRecordMonthView: Record<string, boolean> = {};
      Object.keys(props.bookingsObject).forEach(key => {
        const date = new Date(props.bookingsObject[key].startDate);
        date.setHours(0, 0, 0);
        if (!(date.toDateString() in tempAppointmentLimitMonthView)) {
          tempAppointmentLimitMonthView[date.toDateString()] = 2;
          tempDayScaleRecordMonthView[date.toDateString()] = false;
        }
      });

      let tempBookings: IBookingType[] = checkBookings(
        currentView,
        props.bookingsObject,
        overView,
        tempAppointmentLimitMonthView
      );
      return [
        tempAppointmentLimitMonthView,
        tempDayScaleRecordMonthView,
        tempBookings,
      ];
    }, [currentView, overView, props.bookingsObject]);

  const GetAllDayMessages = (locale: string) => {
    if (weekno !== undefined) {
      return {
        allDay: (
          <span
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              paddingBottom: 10,
            }}
          >
            <span
              style={{
                paddingBottom: 20,
                paddingTop: 20,
                display: 'flex',
                justifyContent: 'center',
              }}
            >{`v ${weekno}`}</span>
            {`${allDayLocalizationMessages[locale].allDay}`}
          </span>
        ),
      };
    }
    return allDayLocalizationMessages[locale];
  };

  const allDayLocalizationMessages: Record<string, { allDay: string }> = {
    'sv-SV': {
      allDay: 'Heldag',
    },
  };

  const resources = useDeepCompareMemo(
    () => [
      {
        fieldName: 'resourceNameList',
        instances:
          props.resources?.map(resource => ({
            id: resource.resName,
            text: resource.resName,
            color: 'transparent',
          })) || [],
        allowMultiple: true,
      },
    ],
    [props.resources]
  );

  const grouping = [{ resourceName: 'resourceNameList' }];

  const onSelectDetail = useCallback(
    (id: TSelectedDetail) => {
      setTooltipVisible(false);
      if (selectDetail) {
        selectDetail(id);
      }
    },
    [selectDetail]
  );
  const changeMooreDayView = useCallback(
    function (data: any): void {
      const currentDate = tryStrToDateTime(data.startDate);

      if (onCurrentDateChange && currentDate) {
        onCurrentDateChange(currentDate);
      }
      if (onCurrentViewChange) {
        onCurrentViewChange('Day');
      }
    },
    [onCurrentDateChange, onCurrentViewChange]
  );

  const onSwipeLeftListener = () => {
    onNavigate?.('forward');
  };
  const onSwipeRightListener = () => {
    onNavigate?.('back');
  };

  return (
    <Swipe
      onSwipedLeft={onSwipeLeftListener}
      onSwipedRight={onSwipeRightListener}
      detectTouch={true}
      style={{
        height: '100%',
        overflowY: 'hidden',
        borderTop: '1px solid rgb(224, 224, 224)',
      }}
      detectMouse={isSmallScreen || isExtraSmallScreen ? true : false}
    >
      <SchedulerContextProvider
        onOverViewChange={props.onOverViewChange}
        currentViewName={props.currentView}
        overView={overView}
        appointmentLimitMonthView={appointmentLimitMonthView}
        dayScaleRecordMonthView={dayScaleRecordMonthView}
        onSelectDetail={onSelectDetail}
        changeMooreDayView={changeMooreDayView}
        setNyAktivitetInitialValues={setNyAktivitetInitialValues}
        resources={props.resources ?? []}
        redDays={props.redDays ?? []}
        bookingGroups={props.bookingGroups}
        setOnNavigate={setOnNavigate}
        onNavigate={onNavigate}
        tooltipOnHover={(tooltip: boolean) => {
          if (tooltip) {
            setTooltipVisible(false);
          }
        }}
        showOutlookBookings={showOutlookBookings}
        setShowOutlookBookings={props.setShowOutlookBookings}
      >
        <Scheduler
          firstDayOfWeek={props.firstDayOfWeek}
          locale={props.messages}
          data={bookings}
          rootComponent={SchedulerRootComponent}
        >
          <ViewState
            currentDate={props.currentDate}
            onCurrentDateChange={props.onCurrentDateChange}
            currentViewName={props.currentView}
            onCurrentViewNameChange={props.onCurrentViewChange}
          />
          {overView && (
            <GroupingState
              grouping={grouping}
              groupOrientation={() => 'Horizontal'}
            />
          )}
          <DayView
            timeScaleLayoutComponent={DayViewTimeScaleLayoutCustom}
            displayName={props.dayViewDisplayName}
            cellDuration={props.dayViewCellDuration}
            endDayHour={24}
            layoutComponent={DayViewLayoutComponent}
            dayScaleCellComponent={DayWeekViewDayScaleCellCustom}
            timeTableLayoutComponent={DayViewTimeTableLayout}
            timeTableCellComponent={DayViewTimeTableCellComponent}
            dayScaleRowComponent={DayScaleRowComponentCustom}
          />
          <WeekView
            displayName={props.weekViewDisplayName}
            endDayHour={24}
            timeTableLayoutComponent={WeekViewTimeTableLayoutCustom}
            dayScaleLayoutComponent={WeekViewDayScaleLayoutCustom}
            dayScaleCellComponent={DayWeekViewDayScaleCellCustom}
            timeScaleLayoutComponent={WeekViewTimeScaleLayoutCustom}
            timeTableCellComponent={WeekViewTimeTableCellComponent}
            cellDuration={props.weekViewCellDuration}
          />
          <MonthView
            displayName={props.monthViewDisplayName}
            dayScaleLayoutComponent={MonthDayScaleLayoutCustom}
            timeTableLayoutComponent={MonthViewTimeTableLayoutProps}
            layoutComponent={CustomMonthLayout}
            timeTableCellComponent={MonthViewTimeTableCellComponent}
            appointmentLayerComponent={CustomMonthAppointmentLayerComponent}
          />
          <Appointments
            appointmentContentComponent={CustomAppointments}
            appointmentComponent={AppointmentComponent}
            containerComponent={
              AppointmentContainer as React.ComponentType<AppointmentsBase.ContainerProps>
            }
          />
          <AppointmentTooltip
            contentComponent={
              CustomTooltipComponent as React.ComponentType<AppointmentTooltipBase.ContentProps>
            }
            layoutComponent={TooltipLayoutComponent}
            visible={tooltipVisible}
            onVisibilityChange={setTooltipVisible}
          />
          <Resources data={resources} />

          {props.showHeader && (
            <Toolbar
              rootComponent={ToolbarRootComponent}
              flexibleSpaceComponent={ToolbarFlexibleSpaceComponent}
            />
          )}
          {props.showHeader && (
            <DateNavigator
              rootComponent={props => (
                <DateNavigatorRootComponent {...props} overview={overView} />
              )}
              navigationButtonComponent={DateNavigationButtonComponent}
              openButtonComponent={DateNavigationOpenButtonComponent}
              overlayComponent={DateNavigationOverlayComponent}
            />
          )}
          {props.showHeader && props.showViewSwitcher && (
            <ViewSwitcher switcherComponent={SwitcherComponent} />
          )}

          <AllDayPanel
            messages={GetAllDayMessages(props.messages) as any}
            titleCellComponent={AllDayTitleCellComponent}
            cellComponent={AllDayCellComponent}
          />
          {overView && <IntegratedGrouping />}
          {overView && (
            <GroupingPanel cellComponent={GroupingPanelCellComponent} />
          )}
          {props.showHeader && (
            <TodayButton
              buttonComponent={TodayButtonComponent}
              messages={{ today: 'Idag' }}
            />
          )}
        </Scheduler>
      </SchedulerContextProvider>
    </Swipe>
  );
};

export default SchedulerComponent;
SchedulerComponent.whyDidYouRender = true;
